import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';

// ─── Supabase Client ───
const SUPABASE_URL = 'https://ijkjmjqnhmubcnhrsqqg.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imlqa2ptanFuaG11YmNuaHJzcXFnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMzMzQzNDcsImV4cCI6MjA4ODkxMDM0N30.hE8W-tQeWLGNvct8TI9SIOy45Vn9E37eYXyBwRKnS24';
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── Storage Layer (Supabase) ───
async function loadData() {
  try {
    const [{ data: slots, error: slotsErr }, { data: requests, error: reqErr }] = await Promise.all([
      supabase.from('slots').select('*'),
      supabase.from('requests').select('*'),
    ]);
    if (slotsErr) throw slotsErr;
    if (reqErr) throw reqErr;

    // Normalize DB column names (snake_case) → app field names (camelCase)
    const normalizedSlots = (slots || []).map(s => ({
      id: s.id,
      date: s.date,
      time: s.time,
      producerId: s.producer_id,
      sessionTypeId: s.session_type_id,
      status: s.status,
      benAvailable: s.ben_available,
    }));
    const normalizedRequests = (requests || []).map(r => ({
      id: r.id,
      slotId: r.slot_id,
      requestedBy: r.requested_by,
      requestedAt: r.requested_at,
      note: r.note,
      status: r.status,
    }));

    return { slots: normalizedSlots, requests: normalizedRequests };
  } catch (e) {
    console.error('Supabase loadData failed:', e);
    return null;
  }
}

async function saveData(data) {
  try {
    // Upsert slots — map camelCase back to snake_case DB columns
    if (data.slots.length > 0) {
      const { error: slotsErr } = await supabase.from('slots').upsert(
        data.slots.map(s => ({
          id: s.id,
          date: s.date,
          time: s.time,
          producer_id: s.producerId,
          session_type_id: s.sessionTypeId,
          status: s.status,
          ben_available: s.benAvailable !== false, // default true
        })),
        { onConflict: 'id' }
      );
      if (slotsErr) throw slotsErr;
    }

    // Upsert requests
    if (data.requests.length > 0) {
      const { error: reqErr } = await supabase.from('requests').upsert(
        data.requests.map(r => ({
          id: r.id,
          slot_id: r.slotId,
          requested_by: r.requestedBy,
          requested_at: r.requestedAt,
          note: r.note,
          status: r.status,
        })),
        { onConflict: 'id' }
      );
      if (reqErr) throw reqErr;
    }
  } catch (e) {
    console.error('Supabase saveData failed:', e);
  }
}

async function deleteSlot(slotId) {
  try {
    const { error } = await supabase.from('slots').delete().eq('id', slotId);
    if (error) throw error;
  } catch (e) {
    console.error('Supabase deleteSlot failed:', e);
  }
}

// ─── Brand Tokens ───
const COLORS = {
  main: '#254338',
  mainLight: '#2e5445',
  mainDark: '#1a3028',
  gold: '#E5B978',
  goldLight: '#f0d4a0',
  goldDark: '#c9982e',
  offWhite: '#FFFCF5',
  bone: '#ECE4D1',
  shadow: '#333333',
  white: '#ffffff',
  red: '#8B2500',
  redLight: '#A63A1A',
  greenSuccess: '#3a7d5c',
};

const ACUITY_BASE = 'https://wtfmediastudios.acuityscheduling.com';
const ACUITY_SCHEDULE = `${ACUITY_BASE}/schedule.php`;

const BEN_PREFILL =
  'firstName=Ben&lastName=Bradbury&email=ben%40benbradbury.com&phone=347-793-1539';

const PRODUCERS = [
  { id: 'wolf-taylor', name: 'Wolf Taylor', preferred: true, calendarID: null },
  { id: 'aamir-augustine', name: 'Aamir Augustine', preferred: false, calendarID: null },
  { id: 'arthur', name: 'Arthur', preferred: false, calendarID: null },
  { id: 'chris-fernandez', name: 'Chris Fernandez', preferred: false, calendarID: null },
  { id: 'dave-jeffers', name: 'Dave Jeffers', preferred: false, calendarID: null },
  { id: 'destinee-clowe', name: 'Destinee Clowe', preferred: false, calendarID: null },
  { id: 'remy-hdz', name: 'Remy Hdz.', preferred: false, calendarID: null },
  { id: 'templeton-wright', name: 'Templeton Wright', preferred: false, calendarID: null },
];

const SESSION_TYPES = [
  {
    id: 'standard',
    label: 'Standard Recording',
    duration: 2,
    cost: 450,
    description: 'Audio & Multi-Angle Video w/ live switching — 2 hrs',
    appointmentTypeID: null,
  },
  {
    id: 'extended',
    label: 'Extended Session',
    duration: 3,
    cost: 675,
    description: 'Audio & Multi-Angle Video w/ live switching — 3 hrs',
    appointmentTypeID: null,
  },
];

function buildSessionTypeLink(sessionType) {
  let url = ACUITY_SCHEDULE + '?' + BEN_PREFILL;
  if (sessionType?.appointmentTypeID) {
    url += `&appointmentType=${sessionType.appointmentTypeID}`;
  }
  return url;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}

function formatTime(t) {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

function isToday(dateStr) {
  const today = new Date();
  return dateStr === getDateStr(today.getFullYear(), today.getMonth(), today.getDate());
}

function isPast(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  return d < today;
}

function timeAgo(isoStr) {
  const now = new Date();
  const then = new Date(isoStr);
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function buildBookingLink(slot) {
  const producer = PRODUCERS.find((p) => p.id === slot.producerId);
  let url = ACUITY_SCHEDULE + '?' + BEN_PREFILL;
  if (producer?.calendarID) url += `&calendarID=${producer.calendarID}`;
  if (slot.date && slot.time) {
    const datetime = `${slot.date}T${slot.time}:00-04:00`;
    url += `&datetime=${encodeURIComponent(datetime)}`;
  }
  return url;
}

function buildFullScheduleLink() {
  return ACUITY_SCHEDULE + '?' + BEN_PREFILL;
}

// ─── Ornamental SVG ───
function OrnamentDivider({ color = COLORS.gold, width = 200 }) {
  return (
    <svg width={width} height="16" viewBox="0 0 200 16" fill="none" style={{ display: 'block', margin: '0 auto' }}>
      <line x1="0" y1="8" x2="70" y2="8" stroke={color} strokeWidth="0.5" />
      <circle cx="80" cy="8" r="2" fill={color} />
      <path d="M 88 8 L 100 3 L 112 8 L 100 13 Z" fill="none" stroke={color} strokeWidth="0.7" />
      <circle cx="100" cy="8" r="1.2" fill={color} />
      <circle cx="120" cy="8" r="2" fill={color} />
      <line x1="130" y1="8" x2="200" y2="8" stroke={color} strokeWidth="0.5" />
    </svg>
  );
}

function FloralCorner({ style = {} }) {
  return (
    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" style={{ opacity: 0.12, ...style }}>
      <path d="M5 55 Q5 5 55 5" stroke={COLORS.gold} strokeWidth="1" fill="none" />
      <path d="M10 55 Q10 10 55 10" stroke={COLORS.gold} strokeWidth="0.5" fill="none" />
      <circle cx="8" cy="8" r="3" stroke={COLORS.gold} strokeWidth="0.5" fill="none" />
      <path d="M3 3 L13 13 M13 3 L3 13" stroke={COLORS.gold} strokeWidth="0.4" />
    </svg>
  );
}

// ─── Shared Components ───
function Button({ children, onClick, variant = 'primary', disabled = false, style = {} }) {
  const base = {
    border: 'none', padding: '10px 24px', borderRadius: '2px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'Geist', sans-serif", fontSize: '13px',
    letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 500,
    transition: 'all 0.3s ease', opacity: disabled ? 0.4 : 1,
  };
  const variants = {
    primary: { background: COLORS.gold, color: COLORS.mainDark, ...base },
    secondary: { background: 'transparent', color: COLORS.gold, border: `1px solid ${COLORS.gold}`, ...base },
    danger: { background: COLORS.red, color: COLORS.offWhite, ...base },
    success: { background: COLORS.greenSuccess, color: COLORS.offWhite, ...base },
    ghost: { background: 'transparent', color: COLORS.bone, ...base, padding: '8px 16px' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...variants[variant], ...style }}>
      {children}
    </button>
  );
}

function Badge({ children, color = COLORS.gold }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '2px',
      fontSize: '10px', letterSpacing: '1.2px', textTransform: 'uppercase',
      fontWeight: 600, background: color + '22', color: color, border: `1px solid ${color}44`,
    }}>
      {children}
    </span>
  );
}

function BenBadge({ available }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', borderRadius: '2px',
      fontSize: '10px', letterSpacing: '1.2px', textTransform: 'uppercase',
      fontWeight: 600,
      background: available ? COLORS.greenSuccess + '22' : COLORS.shadow + '44',
      color: available ? COLORS.greenSuccess : COLORS.bone + '88',
      border: `1px solid ${available ? COLORS.greenSuccess + '44' : COLORS.bone + '22'}`,
    }}>
      {available ? '✓ Ben available' : 'Ben unavailable'}
    </span>
  );
}

function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: COLORS.mainLight + '66', border: `1px solid ${COLORS.gold}22`,
      borderRadius: '3px', padding: '24px', position: 'relative',
      backdropFilter: 'blur(10px)', ...style,
    }}>
      {children}
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block', fontSize: '10px', letterSpacing: '1.5px',
          textTransform: 'uppercase', color: COLORS.bone, marginBottom: '6px',
          fontFamily: "'Geist', sans-serif",
        }}>
          {label}
        </label>
      )}
      <input {...props} style={{
        width: '100%', padding: '10px 14px', background: COLORS.mainDark,
        border: `1px solid ${COLORS.gold}33`, borderRadius: '2px',
        color: COLORS.offWhite, fontSize: '14px', fontFamily: "'Geist', sans-serif",
        outline: 'none', boxSizing: 'border-box', ...(props.style || {}),
      }} />
    </div>
  );
}

function Select({ label, options, ...props }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      {label && (
        <label style={{
          display: 'block', fontSize: '10px', letterSpacing: '1.5px',
          textTransform: 'uppercase', color: COLORS.bone, marginBottom: '6px',
          fontFamily: "'Geist', sans-serif",
        }}>
          {label}
        </label>
      )}
      <select {...props} style={{
        width: '100%', padding: '10px 14px', background: COLORS.mainDark,
        border: `1px solid ${COLORS.gold}33`, borderRadius: '2px',
        color: COLORS.offWhite, fontSize: '14px', fontFamily: "'Geist', sans-serif",
        outline: 'none', boxSizing: 'border-box', ...(props.style || {}),
      }}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Calendar Component ───
function Calendar({ year, month, onMonthChange, selectedDate, onDateSelect, slotsByDate = {}, compact = false }) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: compact ? '12px' : '20px' }}>
        <button onClick={() => onMonthChange(-1)} style={{ background: 'none', border: 'none', color: COLORS.gold, cursor: 'pointer', fontSize: '18px', padding: '4px 12px' }}>‹</button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: compact ? '16px' : '18px', color: COLORS.offWhite, letterSpacing: '1px' }}>
          {MONTHS[month]} {year}
        </span>
        <button onClick={() => onMonthChange(1)} style={{ background: 'none', border: 'none', color: COLORS.gold, cursor: 'pointer', fontSize: '18px', padding: '4px 12px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', textAlign: 'center' }}>
        {DAYS.map((d) => (
          <div key={d} style={{ fontSize: compact ? '9px' : '10px', letterSpacing: '1.5px', color: COLORS.bone, padding: compact ? '6px 0' : '8px 0', fontFamily: "'Geist', sans-serif", textTransform: 'uppercase' }}>
            {compact ? d[0] : d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const dateStr = getDateStr(year, month, day);
          const hasSlots = slotsByDate[dateStr]?.length > 0;
          const isSelected = selectedDate === dateStr;
          const past = isPast(dateStr);
          const today = isToday(dateStr);
          return (
            <button key={dateStr} onClick={() => !past && onDateSelect(dateStr)} style={{
              padding: compact ? '8px 2px' : '10px 4px',
              background: isSelected ? COLORS.gold : 'transparent',
              color: isSelected ? COLORS.mainDark : past ? COLORS.shadow + '66' : COLORS.offWhite,
              border: today ? `1px solid ${COLORS.gold}` : '1px solid transparent',
              borderRadius: '2px', cursor: past ? 'default' : 'pointer',
              fontSize: compact ? '13px' : '14px', fontFamily: "'Geist', sans-serif",
              position: 'relative', transition: 'all 0.2s',
            }}>
              {day}
              {hasSlots && (
                <div style={{ position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '2px' }}>
                  {slotsByDate[dateStr].slice(0, 3).map((s, idx) => (
                    <div key={idx} style={{
                      width: '4px', height: '4px', borderRadius: '50%',
                      background: s.status === 'booked' ? COLORS.greenSuccess : s.status === 'requested' ? COLORS.goldDark : COLORS.gold,
                    }} />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Admin: Add Slot Modal ───
function AddSlotForm({ date, onAdd, onClose }) {
  const [time, setTime] = useState('09:00');
  const [producerId, setProducerId] = useState('wolf-taylor');
  const [sessionTypeId, setSessionTypeId] = useState('standard');
  const [benAvailable, setBenAvailable] = useState(true);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: '20px',
    }}>
      <Card style={{ width: '100%', maxWidth: '420px', padding: '32px', background: COLORS.mainDark, border: `1px solid ${COLORS.gold}44` }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '20px', margin: '0 0 4px 0' }}>
          Add Studio Slot
        </h3>
        <p style={{ color: COLORS.bone, fontSize: '13px', margin: '0 0 24px 0' }}>{formatDate(date)}</p>
        <OrnamentDivider width={160} />
        <div style={{ marginTop: '20px' }}>
          <Input label="Start Time" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          <Select
            label="Producer"
            value={producerId}
            onChange={(e) => setProducerId(e.target.value)}
            options={PRODUCERS.map((p) => ({ value: p.id, label: p.name + (p.preferred ? ' ★' : '') }))}
          />
          <Select
            label="Session Type"
            value={sessionTypeId}
            onChange={(e) => setSessionTypeId(e.target.value)}
            options={SESSION_TYPES.map((s) => ({ value: s.id, label: `${s.label} — ${s.duration}hr — $${s.cost}` }))}
          />
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block', fontSize: '10px', letterSpacing: '1.5px',
              textTransform: 'uppercase', color: COLORS.bone, marginBottom: '10px',
              fontFamily: "'Geist', sans-serif",
            }}>
              Ben's Availability
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ val: true, label: '✓ Available', color: COLORS.greenSuccess }, { val: false, label: 'Unavailable', color: COLORS.bone + '88' }].map(opt => (
                <button
                  key={String(opt.val)}
                  onClick={() => setBenAvailable(opt.val)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '2px', cursor: 'pointer',
                    fontFamily: "'Geist', sans-serif", fontSize: '12px', letterSpacing: '0.5px',
                    transition: 'all 0.2s',
                    background: benAvailable === opt.val
                      ? (opt.val ? COLORS.greenSuccess + '22' : COLORS.shadow + '44')
                      : 'transparent',
                    color: benAvailable === opt.val ? opt.color : COLORS.bone + '55',
                    border: `1px solid ${benAvailable === opt.val
                      ? (opt.val ? COLORS.greenSuccess + '66' : COLORS.bone + '33')
                      : COLORS.gold + '22'}`,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <Button onClick={() => onAdd({ date, time, producerId, sessionTypeId, benAvailable })} style={{ flex: 1 }}>
            Add Slot
          </Button>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
        </div>
      </Card>
    </div>
  );
}

// ─── Admin: Studio Availability ───
function AvailabilityScanner() {
  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <p style={{ color: COLORS.bone, fontSize: '13px', lineHeight: '1.7', marginBottom: '4px' }}>
          Check Soho Studio A availability, then add open slots to the <strong style={{ color: COLORS.gold }}>Calendar</strong> tab.
        </p>
        <p style={{ color: COLORS.bone + '99', fontSize: '12px', lineHeight: '1.6' }}>
          Prioritize <strong style={{ color: COLORS.gold }}>Wolf Taylor</strong> when selecting a producer.
        </p>
      </div>

      <Card style={{ padding: '0', overflow: 'hidden', border: `1px solid ${COLORS.gold}44`, marginBottom: '24px' }}>
        <div style={{ padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <span style={{ color: COLORS.offWhite, fontSize: '18px', fontFamily: "'Playfair Display', serif", display: 'block', marginBottom: '6px' }}>
              WTF Media Studios — Soho
            </span>
            <p style={{ color: COLORS.bone, fontSize: '12px', margin: '0 0 2px 0', lineHeight: '1.5' }}>Audio & Multi-Angle Video w/ live switching</p>
            <p style={{ color: COLORS.bone + 'aa', fontSize: '12px', margin: 0 }}>Standard 2hr ($450) · Extended 3hr ($675)</p>
          </div>
          <a href="https://www.wtfmediastudios.com/book-studio-a" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Button style={{ fontSize: '12px', padding: '14px 28px' }}>Book Soho Studio A ↗</Button>
          </a>
        </div>
      </Card>

      <Card style={{ padding: '16px 20px', marginBottom: '24px', background: COLORS.mainDark }}>
        <span style={{ fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: COLORS.gold, fontFamily: "'Geist', sans-serif" }}>
          Producer Preference
        </span>
        <p style={{ color: COLORS.offWhite, fontSize: '13px', margin: '6px 0 0 0', lineHeight: '1.6' }}>
          Always pick <strong style={{ color: COLORS.gold }}>★ Wolf Taylor</strong> first. If unavailable, any producer works.
        </p>
        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PRODUCERS.map((p) => (
            <span key={p.id} style={{
              padding: '4px 10px', fontSize: '11px', borderRadius: '2px',
              background: p.preferred ? COLORS.gold + '22' : COLORS.mainLight,
              color: p.preferred ? COLORS.gold : COLORS.bone + 'aa',
              border: `1px solid ${p.preferred ? COLORS.gold + '44' : COLORS.gold + '15'}`,
            }}>
              {p.preferred && '★ '}{p.name}
            </span>
          ))}
        </div>
      </Card>

      <Card style={{ padding: '16px 20px', background: COLORS.gold + '0a', border: `1px solid ${COLORS.gold}22` }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '18px', flexShrink: 0 }}>💡</span>
          <div>
            <p style={{ color: COLORS.gold, fontSize: '12px', fontWeight: 600, margin: '0 0 10px 0', letterSpacing: '0.5px' }}>Booking Workflow</p>
            {[
              'Check availability above (pick Wolf Taylor first)',
              "Switch to Calendar tab and add open slots — mark Ben's availability on each",
              "For any slots you're not sure of, ask Ben directly",
              'Approve in Requests',
              'Click "Book on WTF" to book/complete payment',
            ].map((step, i) => (
              <p key={i} style={{ color: COLORS.bone, fontSize: '12px', lineHeight: '1.5', margin: '0 0 6px 0' }}>
                <strong style={{ color: COLORS.offWhite }}>{i + 1}.</strong> {step}
              </p>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── Admin View ───
function AdminView({ data, setData, saveAll }) {
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [tab, setTab] = useState('scanner');

  function handleMonthChange(delta) {
    let m = calMonth + delta, y = calYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCalMonth(m); setCalYear(y);
  }

  function addSlot(slotInfo) {
    const newSlot = { id: generateId(), ...slotInfo, status: 'available' };
    const updated = { ...data, slots: [...data.slots, newSlot] };
    setData(updated); saveAll(updated); setShowAddSlot(false);
  }

  // ─── removeSlot: also deletes from Supabase ───
  function removeSlot(slotId) {
    deleteSlot(slotId); // delete from DB
    const updated = { ...data, slots: data.slots.filter((s) => s.id !== slotId) };
    setData(updated); saveAll(updated);
  }

  function toggleBenAvailable(slotId) {
    const updated = {
      ...data,
      slots: data.slots.map((s) =>
        s.id === slotId ? { ...s, benAvailable: s.benAvailable === false ? true : false } : s
      ),
    };
    setData(updated); saveAll(updated);
  }

  function handleRequest(requestId, action) {
    const updated = {
      ...data,
      requests: data.requests.map((r) => r.id === requestId ? { ...r, status: action } : r),
      slots: data.slots.map((s) => {
        const req = data.requests.find((r) => r.id === requestId);
        if (req && s.id === req.slotId) {
          return { ...s, status: action === 'approved' ? 'booked' : 'available' };
        }
        return s;
      }),
    };
    setData(updated); saveAll(updated);
  }

  function markConfirmed(slotId) {
    const updated = { ...data, slots: data.slots.map((s) => s.id === slotId ? { ...s, status: 'confirmed' } : s) };
    setData(updated); saveAll(updated);
  }

  const slotsByDate = useMemo(() => {
    const map = {};
    data.slots.forEach((s) => { if (!map[s.date]) map[s.date] = []; map[s.date].push(s); });
    return map;
  }, [data.slots]);

  const pendingRequests = data.requests.filter((r) => r.status === 'pending');
  const approvedRequests = data.requests.filter((r) => r.status === 'approved');
  const selectedSlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  const bookedStandard = data.slots.filter((s) => (s.status === 'booked' || s.status === 'confirmed') && s.sessionTypeId === 'standard').length;
  const bookedExtended = data.slots.filter((s) => (s.status === 'booked' || s.status === 'confirmed') && s.sessionTypeId === 'extended').length;

  return (
    <div>
      <Card style={{ padding: '16px 20px', marginBottom: '24px', background: COLORS.mainDark }}>
        <span style={{ fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: COLORS.gold, fontFamily: "'Geist', sans-serif", display: 'block', marginBottom: '10px' }}>
          Season 2 Session Booking Target
        </span>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: COLORS.offWhite }}>Standard 2-hour bookings:</span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: bookedStandard >= 3 ? COLORS.greenSuccess : COLORS.gold }}>{bookedStandard}/3</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: COLORS.offWhite }}>Extended 3-hour booking:</span>
            <span style={{ fontSize: '14px', fontWeight: 600, color: bookedExtended >= 1 ? COLORS.greenSuccess : COLORS.gold }}>{bookedExtended}/1</span>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: '0', marginBottom: '32px', borderBottom: `1px solid ${COLORS.gold}33`, overflowX: 'auto' }}>
        {[
          { id: 'scanner', label: 'Studio Availability', icon: '🔍' },
          { id: 'calendar', label: 'Your Calendar', icon: '📅' },
          { id: 'requests', label: 'Requests', icon: '📬', count: pendingRequests.length || null },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none',
            borderBottom: tab === t.id ? `2px solid ${COLORS.gold}` : '2px solid transparent',
            color: tab === t.id ? COLORS.gold : COLORS.bone,
            padding: '12px 24px', fontSize: '11px', letterSpacing: '1.8px', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: "'Geist', sans-serif",
            display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            <span>{t.icon}</span> {t.label}
            {t.count && (
              <span style={{ background: COLORS.gold, color: COLORS.mainDark, borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700 }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'scanner' && <AvailabilityScanner />}

      {tab === 'calendar' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          <Card>
            <Calendar year={calYear} month={calMonth} onMonthChange={handleMonthChange} selectedDate={selectedDate} onDateSelect={setSelectedDate} slotsByDate={slotsByDate} />
            <div style={{ marginTop: '12px', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {[{ color: COLORS.gold, label: 'Available' }, { color: COLORS.goldDark, label: 'Requested' }, { color: COLORS.greenSuccess, label: 'Booked/Confirmed' }].map((item) => (
                <span key={item.label} style={{ fontSize: '10px', color: COLORS.bone, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.color, display: 'inline-block' }} /> {item.label}
                </span>
              ))}
            </div>
          </Card>
          <div>
            {selectedDate ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '18px', margin: 0 }}>{formatDate(selectedDate)}</h3>
                  <Button onClick={() => setShowAddSlot(true)} style={{ fontSize: '11px', padding: '8px 16px' }}>+ Add Slot</Button>
                </div>
                {selectedSlots.length === 0 ? (
                  <Card style={{ textAlign: 'center', padding: '40px 24px' }}>
                    <p style={{ color: COLORS.bone, fontSize: '14px', margin: 0 }}>No studio slots added for this date.</p>
                    <p style={{ color: COLORS.gold, fontSize: '12px', margin: '8px 0 0 0', cursor: 'pointer' }} onClick={() => setShowAddSlot(true)}>Click to add available studio time →</p>
                  </Card>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {selectedSlots.map((slot) => {
                      const producer = PRODUCERS.find((p) => p.id === slot.producerId);
                      const session = SESSION_TYPES.find((s) => s.id === slot.sessionTypeId);
                      const request = data.requests.find((r) => r.slotId === slot.id && r.status === 'pending');
                      const approved = data.requests.find((r) => r.slotId === slot.id && r.status === 'approved');
                      const isBooked = slot.status === 'booked';
                      const isConfirmed = slot.status === 'confirmed';
                      const benAvail = slot.benAvailable !== false;
                      return (
                        <Card key={slot.id} style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                <span style={{ color: COLORS.offWhite, fontSize: '16px', fontFamily: "'Playfair Display', serif" }}>{formatTime(slot.time)}</span>
                                <Badge color={isConfirmed ? COLORS.greenSuccess : isBooked ? COLORS.greenSuccess : slot.status === 'requested' ? COLORS.goldDark : COLORS.gold}>
                                  {isConfirmed ? 'Confirmed' : slot.status}
                                </Badge>
                                <BenBadge available={benAvail} />
                              </div>
                              <p style={{ color: COLORS.bone, fontSize: '12px', margin: '0 0 2px 0' }}>{producer?.name}{producer?.preferred ? ' ★' : ''} · {session?.label}</p>
                              <p style={{ color: COLORS.gold + '99', fontSize: '11px', margin: 0 }}>${session?.cost}</p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => toggleBenAvailable(slot.id)}
                                title="Toggle Ben's availability"
                                style={{
                                  background: 'none', border: `1px solid ${COLORS.gold}33`,
                                  color: benAvail ? COLORS.greenSuccess : COLORS.bone + '66',
                                  cursor: 'pointer', fontSize: '11px', padding: '4px 10px',
                                  borderRadius: '2px', fontFamily: "'Geist', sans-serif",
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {benAvail ? '✓ Ben' : '✗ Ben'}
                              </button>
                              {request && (
                                <>
                                  <Button onClick={() => handleRequest(request.id, 'approved')} style={{ fontSize: '10px', padding: '6px 12px' }}>Approve</Button>
                                  <Button variant="danger" onClick={() => handleRequest(request.id, 'declined')} style={{ fontSize: '10px', padding: '6px 12px' }}>Decline</Button>
                                </>
                              )}
                              {isBooked && !isConfirmed && (
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <a href={buildBookingLink(slot)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                    <Button variant="success" style={{ fontSize: '10px', padding: '6px 12px' }}>Book on WTF →</Button>
                                  </a>
                                  <Button variant="ghost" onClick={() => markConfirmed(slot.id)} style={{ fontSize: '10px', padding: '6px 10px', color: COLORS.greenSuccess }}>✓ Done</Button>
                                </div>
                              )}
                              {isConfirmed && <span style={{ fontSize: '11px', color: COLORS.greenSuccess }}>✓ Booked & confirmed</span>}
                              {slot.status === 'available' && (
                                <button onClick={() => removeSlot(slot.id)} style={{ background: 'none', border: 'none', color: COLORS.red, cursor: 'pointer', fontSize: '18px' }}>×</button>
                              )}
                            </div>
                          </div>
                          {request && (
                            <div style={{ marginTop: '10px', padding: '8px 12px', background: COLORS.gold + '11', borderRadius: '2px' }}>
                              <span style={{ fontSize: '11px', color: COLORS.gold }}>Requested by {request.requestedBy}</span>
                              {request.note && <p style={{ fontSize: '12px', color: COLORS.bone, margin: '4px 0 0 0', fontStyle: 'italic' }}>"{request.note}"</p>}
                            </div>
                          )}
                          {approved && !isConfirmed && (
                            <div style={{ marginTop: '10px', padding: '8px 12px', background: COLORS.greenSuccess + '15', borderRadius: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: COLORS.greenSuccess }}>✓ Approved for {approved.requestedBy}</span>
                              <a href={buildBookingLink(slot)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                <span style={{ fontSize: '11px', color: COLORS.gold, cursor: 'pointer', letterSpacing: '1px', textTransform: 'uppercase' }}>Complete booking →</span>
                              </a>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <Card style={{ textAlign: 'center', padding: '60px 24px' }}>
                <p style={{ color: COLORS.bone, fontSize: '14px', margin: '0 0 8px 0' }}>Select a date to manage studio availability</p>
                <p style={{ color: COLORS.gold + '88', fontSize: '12px', margin: 0 }}>Gold dots = available · Yellow = requested · Green = booked</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {tab === 'requests' && (
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '16px', margin: '0 0 16px 0' }}>Pending Requests</h3>
          {pendingRequests.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px 24px', marginBottom: '32px' }}>
              <p style={{ color: COLORS.bone, fontSize: '13px', margin: 0 }}>No pending requests. When Jon or Peter select a slot, it will appear here.</p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
              {pendingRequests.map((req) => {
                const slot = data.slots.find((s) => s.id === req.slotId);
                const producer = slot ? PRODUCERS.find((p) => p.id === slot.producerId) : null;
                const session = slot ? SESSION_TYPES.find((s) => s.id === slot.sessionTypeId) : null;
                return (
                  <Card key={req.id} style={{ padding: '20px 24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <span style={{ color: COLORS.offWhite, fontFamily: "'Playfair Display', serif", fontSize: '16px' }}>
                          {slot ? formatDate(slot.date) : 'Unknown'} at {slot ? formatTime(slot.time) : ''}
                        </span>
                        <p style={{ color: COLORS.bone, fontSize: '12px', margin: '6px 0 2px 0' }}>
                          Requested by <strong style={{ color: COLORS.gold }}>{req.requestedBy}</strong> · {timeAgo(req.requestedAt)}
                        </p>
                        <p style={{ color: COLORS.bone, fontSize: '12px', margin: 0 }}>{producer?.name} · {session?.label} · ${session?.cost}</p>
                        {req.note && <p style={{ color: COLORS.bone + 'cc', fontSize: '12px', margin: '6px 0 0 0', fontStyle: 'italic' }}>"{req.note}"</p>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button onClick={() => handleRequest(req.id, 'approved')} style={{ fontSize: '10px', padding: '8px 16px' }}>Approve</Button>
                        <Button variant="danger" onClick={() => handleRequest(req.id, 'declined')} style={{ fontSize: '10px', padding: '8px 16px' }}>Decline</Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '16px', margin: '0 0 16px 0' }}>Approved — Ready to Book</h3>
          {approvedRequests.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px 24px', marginBottom: '32px' }}>
              <p style={{ color: COLORS.bone, fontSize: '13px', margin: 0 }}>No approved sessions awaiting booking.</p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px' }}>
              {approvedRequests.map((req) => {
                const slot = data.slots.find((s) => s.id === req.slotId);
                const producer = slot ? PRODUCERS.find((p) => p.id === slot.producerId) : null;
                const session = slot ? SESSION_TYPES.find((s) => s.id === slot.sessionTypeId) : null;
                return (
                  <Card key={req.id} style={{ padding: '20px 24px', border: `1px solid ${COLORS.greenSuccess}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <span style={{ color: COLORS.offWhite, fontFamily: "'Playfair Display', serif", fontSize: '16px' }}>
                            {slot ? formatDate(slot.date) : 'Unknown'} at {slot ? formatTime(slot.time) : ''}
                          </span>
                          <Badge color={COLORS.greenSuccess}>Approved</Badge>
                        </div>
                        <p style={{ color: COLORS.bone, fontSize: '12px', margin: 0 }}>{req.requestedBy} · {producer?.name} · {session?.label} · ${session?.cost}</p>
                      </div>
                      <a href={slot ? buildBookingLink(slot) : '#'} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <Button variant="success" style={{ fontSize: '11px', padding: '10px 20px' }}>Book on WTF →</Button>
                      </a>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {data.requests.filter((r) => r.status === 'declined').length > 0 && (
            <>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.bone + '88', fontSize: '14px', margin: '0 0 12px 0' }}>Declined</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {data.requests.filter((r) => r.status === 'declined').map((req) => {
                  const slot = data.slots.find((s) => s.id === req.slotId);
                  return (
                    <Card key={req.id} style={{ padding: '12px 16px', opacity: 0.5 }}>
                      <span style={{ color: COLORS.bone, fontSize: '12px' }}>
                        {slot ? `${formatDateShort(slot.date)} at ${formatTime(slot.time)}` : 'Unknown'} — {req.requestedBy}
                      </span>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {showAddSlot && selectedDate && (
        <AddSlotForm date={selectedDate} onAdd={addSlot} onClose={() => setShowAddSlot(false)} />
      )}
    </div>
  );
}

// ─── Client View ───
function ClientView({ data, setData, saveAll }) {
  const today = new Date();
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [requestName, setRequestName] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  function handleMonthChange(delta) {
    let m = calMonth + delta, y = calYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCalMonth(m); setCalYear(y);
  }

  const availableSlots = data.slots.filter((s) => s.status === 'available');
  const slotsByDate = useMemo(() => {
    const map = {};
    availableSlots.forEach((s) => { if (!map[s.date]) map[s.date] = []; map[s.date].push(s); });
    return map;
  }, [availableSlots]);

  const selectedSlots = selectedDate ? slotsByDate[selectedDate] || [] : [];

  function submitRequest() {
    if (!selectedSlotId || !requestName.trim()) return;
    const newRequest = { id: generateId(), slotId: selectedSlotId, requestedBy: requestName.trim(), requestedAt: new Date().toISOString(), note: requestNote.trim(), status: 'pending' };
    const updated = { ...data, requests: [...data.requests, newRequest], slots: data.slots.map((s) => s.id === selectedSlotId ? { ...s, status: 'requested' } : s) };
    setData(updated); saveAll(updated); setSubmitted(true);
  }

  const upcomingSlots = availableSlots.filter((s) => !isPast(s.date)).sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  const wolfSlots = upcomingSlots.filter((s) => s.producerId === 'wolf-taylor');
  const otherSlots = upcomingSlots.filter((s) => s.producerId !== 'wolf-taylor');

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: COLORS.greenSuccess + '22', border: `2px solid ${COLORS.greenSuccess}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
          <span style={{ fontSize: '28px', color: COLORS.greenSuccess }}>✓</span>
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '24px', margin: '0 0 12px 0' }}>Session Requested</h2>
        <OrnamentDivider />
        <p style={{ color: COLORS.bone, fontSize: '14px', maxWidth: '420px', margin: '20px auto', lineHeight: '1.7' }}>
          Ben has been notified and will confirm the booking shortly. You'll receive a confirmation once the studio session is locked in.
        </p>
        <Button onClick={() => { setSubmitted(false); setSelectedSlotId(null); setRequestName(''); setRequestNote(''); }} style={{ marginTop: '16px' }}>
          Request Another Session
        </Button>
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: COLORS.bone, fontSize: '13px', lineHeight: '1.6', maxWidth: '600px', marginBottom: '24px' }}>
        Select an available studio slot below to request a recording session at WTF Media Studios (Studio A).
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[{ val: false, label: 'Upcoming Slots' }, { val: true, label: 'Calendar View' }].map(opt => (
          <button key={String(opt.val)} onClick={() => setShowCalendar(opt.val)} style={{
            padding: '8px 16px',
            background: showCalendar === opt.val ? COLORS.gold + '22' : 'transparent',
            border: `1px solid ${showCalendar === opt.val ? COLORS.gold + '66' : COLORS.gold + '33'}`,
            color: showCalendar === opt.val ? COLORS.gold : COLORS.bone,
            borderRadius: '2px', cursor: 'pointer', fontSize: '11px', letterSpacing: '1px',
            textTransform: 'uppercase', fontFamily: "'Geist', sans-serif",
          }}>
            {opt.label}
          </button>
        ))}
      </div>

      {showCalendar ? (
        <div>
          <Card style={{ maxWidth: '400px', marginBottom: '24px' }}>
            <Calendar year={calYear} month={calMonth} onMonthChange={handleMonthChange} selectedDate={selectedDate} onDateSelect={(d) => { setSelectedDate(d); setSelectedSlotId(null); }} slotsByDate={slotsByDate} compact={false} />
            <div style={{ marginTop: '12px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <span style={{ fontSize: '10px', color: COLORS.bone, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: COLORS.gold, display: 'inline-block' }} /> Available
              </span>
            </div>
          </Card>
          {selectedDate && (
            <div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '18px', margin: '0 0 16px 0' }}>{formatDate(selectedDate)}</h3>
              {selectedSlots.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <p style={{ color: COLORS.bone, fontSize: '14px' }}>No available slots on this date.</p>
                </Card>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {selectedSlots.sort((a, b) => {
                    if (a.producerId === 'wolf-taylor' && b.producerId !== 'wolf-taylor') return -1;
                    if (b.producerId === 'wolf-taylor' && a.producerId !== 'wolf-taylor') return 1;
                    return a.time.localeCompare(b.time);
                  }).map((slot) => renderSlotCard(slot))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          {upcomingSlots.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px 24px' }}>
              <p style={{ color: COLORS.bone, fontSize: '14px' }}>No upcoming studio slots available yet.</p>
              <p style={{ color: COLORS.gold + '88', fontSize: '12px' }}>Check back soon or contact Ben directly.</p>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {wolfSlots.length > 0 && (
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: COLORS.gold, fontFamily: "'Geist', sans-serif" }}>★ Wolf Taylor — Preferred Producer</span>
                </div>
              )}
              {wolfSlots.map((slot) => renderSlotCard(slot))}
              {otherSlots.length > 0 && (
                <>
                  <div style={{ margin: '12px 0 4px 0' }}>
                    <span style={{ fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: COLORS.bone, fontFamily: "'Geist', sans-serif" }}>Other Producers</span>
                  </div>
                  {otherSlots.map((slot) => renderSlotCard(slot))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {selectedSlotId && (
        <Card style={{ marginTop: '24px', padding: '24px' }}>
          <h4 style={{ fontFamily: "'Playfair Display', serif", color: COLORS.offWhite, fontSize: '16px', margin: '0 0 16px 0' }}>Request This Slot</h4>
          <Input label="Your Name" type="text" value={requestName} onChange={(e) => setRequestName(e.target.value)} placeholder="e.g. Jon Lombardo" />
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase', color: COLORS.bone, marginBottom: '6px', fontFamily: "'Geist', sans-serif" }}>Note (optional)</label>
            <textarea value={requestNote} onChange={(e) => setRequestNote(e.target.value)} placeholder="e.g. Need to record episodes 7 & 8" rows={3} style={{ width: '100%', padding: '10px 14px', background: COLORS.mainDark, border: `1px solid ${COLORS.gold}33`, borderRadius: '2px', color: COLORS.offWhite, fontSize: '14px', fontFamily: "'Geist', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <Button onClick={submitRequest} disabled={!requestName.trim()} style={{ width: '100%' }}>Request Session</Button>
        </Card>
      )}
    </div>
  );

  function renderSlotCard(slot) {
    const producer = PRODUCERS.find((p) => p.id === slot.producerId);
    const session = SESSION_TYPES.find((s) => s.id === slot.sessionTypeId);
    const isSelected = selectedSlotId === slot.id;
    const benAvail = slot.benAvailable !== false;
    return (
      <Card key={slot.id} onClick={() => setSelectedSlotId(isSelected ? null : slot.id)} style={{
        padding: '16px 20px', cursor: 'pointer',
        border: isSelected ? `2px solid ${COLORS.gold}` : `1px solid ${COLORS.gold}22`,
        background: isSelected ? COLORS.gold + '15' : undefined,
        transition: 'all 0.2s',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: COLORS.offWhite, fontSize: '15px', fontFamily: "'Playfair Display', serif" }}>{formatDate(slot.date)}</span>
              {producer?.preferred && <Badge color={COLORS.gold}>Preferred</Badge>}
              <BenBadge available={benAvail} />
            </div>
            <p style={{ color: COLORS.bone, fontSize: '12px', margin: '6px 0 0 0' }}>
              {formatTime(slot.time)} · {producer?.name} · {session?.duration}hr session
            </p>
          </div>
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
            border: `2px solid ${isSelected ? COLORS.gold : COLORS.bone + '44'}`,
            background: isSelected ? COLORS.gold : 'transparent', transition: 'all 0.2s',
          }} />
        </div>
      </Card>
    );
  }
}

// ─── Main App ───
export default function StudioScheduler() {
  const [view, setView] = useState('client');
  const [data, setData] = useState({ slots: [], requests: [] });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function init() {
      const stored = await loadData();
      if (stored) setData(stored);
      setLoaded(true);
    }
    init();
  }, []);

  const saveAll = useCallback(async (newData) => {
    await saveData(newData);
  }, []);

  if (!loaded) {
    return (
      <div style={{ minHeight: '100vh', background: COLORS.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: COLORS.gold, fontFamily: "'Playfair Display', serif", fontSize: '18px', letterSpacing: '2px' }}>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Geist:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { margin: 0; background: ${COLORS.main}; }
        ::selection { background: ${COLORS.gold}44; color: ${COLORS.offWhite}; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${COLORS.mainDark}; }
        ::-webkit-scrollbar-thumb { background: ${COLORS.gold}44; border-radius: 3px; }
        @media (max-width: 768px) { .admin-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div style={{ minHeight: '100vh', background: `linear-gradient(165deg, ${COLORS.mainDark} 0%, ${COLORS.main} 40%, ${COLORS.mainLight}88 100%)`, color: COLORS.offWhite, fontFamily: "'Geist', sans-serif", position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'fixed', inset: 0, backgroundImage: `radial-gradient(circle at 20% 80%, ${COLORS.gold}08 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${COLORS.gold}05 0%, transparent 50%)`, pointerEvents: 'none' }} />
        <header style={{ position: 'relative', borderBottom: `1px solid ${COLORS.gold}22`, padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1200px', margin: '0 auto', padding: '16px 0', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', fontWeight: 600, color: COLORS.offWhite, letterSpacing: '0.5px', margin: 0 }}>Lab-Grown Marketing</h1>
              <p style={{ fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase', color: COLORS.gold, margin: '2px 0 0 0' }}>Studio Scheduler</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {[{ id: 'client', label: 'Book a Session' }, { id: 'admin', label: 'Admin' }].map((v) => (
                <button key={v.id} onClick={() => setView(v.id)} style={{
                  background: view === v.id ? COLORS.gold + '22' : 'transparent',
                  border: `1px solid ${view === v.id ? COLORS.gold + '66' : 'transparent'}`,
                  color: view === v.id ? COLORS.gold : COLORS.bone,
                  padding: '8px 16px', borderRadius: '2px', cursor: 'pointer',
                  fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase',
                  fontFamily: "'Geist', sans-serif", transition: 'all 0.2s',
                }}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </header>
        <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px', position: 'relative' }}>
          <FloralCorner style={{ position: 'absolute', top: '20px', left: '20px' }} />
          <FloralCorner style={{ position: 'absolute', top: '20px', right: '20px', transform: 'scaleX(-1)' }} />
          {view === 'admin' ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: 400, color: COLORS.offWhite, margin: '0 0 8px 0' }}>Production Dashboard</h2>
                <OrnamentDivider />
                <p style={{ color: COLORS.bone, fontSize: '13px', marginTop: '12px' }}>Manage WTF Media Studios availability · Studio A · 265 Canal St, Suite 515, NYC</p>
              </div>
              <AdminView data={data} setData={setData} saveAll={saveAll} />
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: 400, color: COLORS.offWhite, margin: '0 0 8px 0' }}>Book a Recording Session</h2>
                <OrnamentDivider />
                <p style={{ color: COLORS.bone, fontSize: '13px', marginTop: '12px' }}>WTF Media Studios · Studio A · 265 Canal St, Suite 515, NYC</p>
              </div>
              <ClientView data={data} setData={setData} saveAll={saveAll} />
            </>
          )}
        </main>
        <footer style={{ borderTop: `1px solid ${COLORS.gold}15`, padding: '24px', textAlign: 'center', marginTop: '60px' }}>
          <p style={{ fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: COLORS.bone + '66' }}>Lab-Grown Marketing · Presented by Evidenza</p>
        </footer>
      </div>
    </>
  );
}